import axios, { AxiosInstance } from 'axios';
import {
  DatabaseCluster,
  CreateDatabaseRequest,
  DatabaseComponent,
  RestoreRequest,
  DatabaseEngine,
} from '../types';

export class DatabaseService {
  private client: AxiosInstance;

  constructor(baseUrl: string = 'http://localhost:8080/api') {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Get all database clusters
  async getClusters(namespace?: string): Promise<DatabaseCluster[]> {
    try {
      const url = namespace
        ? `/v1/namespaces/${namespace}/database-clusters`
        : `/v1/database-clusters`;
      const response = await this.client.get(url);
      return response.data;
    } catch (_error) {
      // Mock data for development
      console.warn(
        'Falling back to mock data: API call to get database clusters failed',
        _error,
      );
      return this.getMockClusters();
    }
  }

  // Get single database cluster
  async getCluster(namespace: string, name: string): Promise<DatabaseCluster> {
    try {
      const response = await this.client.get(
        `/v1/namespaces/${namespace}/database-clusters/${name}`,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to get database cluster '${namespace}/${name}' failed`,
        _error,
      );
      const mockCluster = this.getMockClusters().find(
        c => c.metadata.name === name && c.metadata.namespace === namespace,
      );
      if (!mockCluster) {
        throw new Error(`Database cluster '${namespace}/${name}' not found`);
      }
      return mockCluster;
    }
  }

  // Create database cluster
  async createCluster(
    request: CreateDatabaseRequest,
  ): Promise<DatabaseCluster> {
    const cluster: DatabaseCluster = {
      metadata: {
        name: request.name,
        namespace: request.namespace,
      },
      spec: {
        engine: {
          type: request.engine,
          version: request.version,
          replicas: request.replicas,
          resources: {
            cpu: `${request.cpu}`,
            memory: `${request.memory}Gi`,
          },
          storage: {
            class: request.storageClass,
            size: `${request.storageSize}Gi`,
          },
        },
        backup: {
          enabled: request.enableBackups || false,
          schedules: request.backupSchedule
            ? [
                {
                  name: 'daily-backup',
                  enabled: true,
                  schedule: request.backupSchedule,
                  backupStorageName: request.backupStorage || '',
                  retentionCopies: 7,
                },
              ]
            : [],
        },
        monitoring: {
          enabled: request.enableMonitoring || false,
          monitoringConfigName: request.monitoringEndpoint,
        },
      },
    };

    try {
      const response = await this.client.post(
        `/v1/namespaces/${request.namespace}/database-clusters`,
        cluster,
      );
      return response.data;
    } catch (_error) {
      // Return mock created cluster
      console.warn(
        'Falling back to mock data: API call to create database cluster failed',
        _error,
      );
      return {
        ...cluster,
        status: {
          status: 'provisioning',
          message: 'Creating database cluster...',
        },
      };
    }
  }

  // Delete database cluster
  async deleteCluster(namespace: string, name: string): Promise<void> {
    try {
      await this.client.delete(
        `/v1/namespaces/${namespace}/database-clusters/${name}`,
      );
    } catch (_error) {
      console.log(`Mock: Deleted cluster ${namespace}/${name}`);
    }
  }

  // Update database cluster
  async updateCluster(
    namespace: string,
    name: string,
    cluster: Partial<DatabaseCluster>,
  ): Promise<DatabaseCluster> {
    try {
      const response = await this.client.patch(
        `/v1/namespaces/${namespace}/database-clusters/${name}`,
        cluster,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to update database cluster '${namespace}/${name}' failed`,
        _error,
      );
      return this.getMockClusters()[0];
    }
  }

  // Pause/Resume database cluster
  async pauseCluster(namespace: string, name: string): Promise<void> {
    try {
      await this.client.post(
        `/v1/namespaces/${namespace}/database-clusters/${name}/pause`,
      );
    } catch (_error) {
      console.log(`Mock: Paused cluster ${namespace}/${name}`);
    }
  }

  async resumeCluster(namespace: string, name: string): Promise<void> {
    try {
      await this.client.post(
        `/v1/namespaces/${namespace}/database-clusters/${name}/resume`,
      );
    } catch (_error) {
      console.log(`Mock: Resumed cluster ${namespace}/${name}`);
    }
  }

  // Get database cluster components
  async getClusterComponents(
    namespace: string,
    name: string,
  ): Promise<DatabaseComponent[]> {
    try {
      const response = await this.client.get(
        `/v1/namespaces/${namespace}/database-clusters/${name}/components`,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to get components for database cluster '${namespace}/${name}' failed`,
        _error,
      );
      return this.getMockComponents();
    }
  }

  // Restart database cluster
  async restartCluster(namespace: string, name: string): Promise<void> {
    try {
      await this.client.post(
        `/v1/namespaces/${namespace}/database-clusters/${name}/restart`,
      );
    } catch (_error) {
      console.log(`Mock: Restarted cluster ${namespace}/${name}`);
    }
  }

  // Restore from backup
  async restoreFromBackup(request: RestoreRequest): Promise<void> {
    try {
      await this.client.post(
        `/v1/namespaces/${request.namespace}/database-clusters/${request.dbClusterName}/restore`,
        request,
      );
    } catch (_error) {
      console.log(`Mock: Restored cluster ${request.dbClusterName}`);
    }
  }

  // Get available database versions
  async getAvailableVersions(engine: DatabaseEngine): Promise<string[]> {
    try {
      const response = await this.client.get(
        `/v1/database-engines/${engine}/versions`,
      );
      return response.data.map((v: any) => v.version);
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to get available versions for engine '${engine}' failed`,
        _error,
      );
      return this.getMockVersions(engine);
    }
  }

  // Mock data helpers
  private getMockClusters(): DatabaseCluster[] {
    return [
      {
        metadata: {
          name: 'pg-production',
          namespace: 'everest',
          creationTimestamp: '2025-01-15T10:00:00Z',
          uid: 'pg-001',
        },
        spec: {
          engine: {
            type: DatabaseEngine.POSTGRESQL,
            version: '16.1',
            replicas: 3,
            resources: {
              cpu: '2',
              memory: '4Gi',
            },
            storage: {
              class: 'standard',
              size: '100Gi',
            },
          },
          proxy: {
            type: 'haproxy',
            replicas: 2,
            expose: {
              type: 'internal',
            },
          },
          backup: {
            enabled: true,
            schedules: [
              {
                name: 'daily-backup',
                enabled: true,
                schedule: '0 2 * * *',
                backupStorageName: 's3-backup',
                retentionCopies: 7,
              },
            ],
          },
          monitoring: {
            enabled: true,
            monitoringConfigName: 'pmm-monitoring',
          },
        },
        status: {
          status: 'ready',
          hostname: 'pg-production-haproxy.everest.svc.cluster.local',
          port: 5432,
          ready: 3,
          size: 3,
          activeStorage: '45Gi',
        },
      },
      {
        metadata: {
          name: 'mysql-dev',
          namespace: 'everest',
          creationTimestamp: '2025-01-20T14:30:00Z',
          uid: 'mysql-001',
        },
        spec: {
          engine: {
            type: DatabaseEngine.MYSQL,
            version: '8.0.35',
            replicas: 2,
            resources: {
              cpu: '1',
              memory: '2Gi',
            },
            storage: {
              size: '50Gi',
            },
          },
          proxy: {
            type: 'proxysql',
            replicas: 1,
          },
          backup: {
            enabled: false,
          },
          monitoring: {
            enabled: false,
          },
        },
        status: {
          status: 'ready',
          hostname: 'mysql-dev-proxysql.everest.svc.cluster.local',
          port: 3306,
          ready: 2,
          size: 2,
          activeStorage: '12Gi',
        },
      },
      {
        metadata: {
          name: 'mongo-analytics',
          namespace: 'everest',
          creationTimestamp: '2025-01-25T09:15:00Z',
          uid: 'mongo-001',
        },
        spec: {
          engine: {
            type: DatabaseEngine.MONGODB,
            version: '7.0.4',
            replicas: 3,
            resources: {
              cpu: '4',
              memory: '8Gi',
            },
            storage: {
              size: '200Gi',
            },
          },
          backup: {
            enabled: true,
            schedules: [
              {
                name: 'hourly-backup',
                enabled: true,
                schedule: '0 * * * *',
                backupStorageName: 's3-backup',
                retentionCopies: 24,
              },
            ],
          },
          monitoring: {
            enabled: true,
            monitoringConfigName: 'pmm-monitoring',
          },
        },
        status: {
          status: 'ready',
          hostname: 'mongo-analytics.everest.svc.cluster.local',
          port: 27017,
          ready: 3,
          size: 3,
          activeStorage: '87Gi',
        },
      },
    ];
  }

  private getMockComponents(): DatabaseComponent[] {
    return [
      {
        name: 'pg-production-0',
        type: 'database',
        status: 'running',
        restarts: 0,
        age: '10d',
        node: 'node-1',
      },
      {
        name: 'pg-production-1',
        type: 'database',
        status: 'running',
        restarts: 1,
        age: '10d',
        node: 'node-2',
      },
      {
        name: 'pg-production-2',
        type: 'database',
        status: 'running',
        restarts: 0,
        age: '10d',
        node: 'node-3',
      },
      {
        name: 'pg-production-haproxy-0',
        type: 'proxy',
        status: 'running',
        restarts: 0,
        age: '10d',
        node: 'node-1',
      },
      {
        name: 'pg-production-haproxy-1',
        type: 'proxy',
        status: 'running',
        restarts: 0,
        age: '10d',
        node: 'node-2',
      },
      {
        name: 'pg-production-backup-agent',
        type: 'backup-agent',
        status: 'running',
        restarts: 0,
        age: '10d',
        node: 'node-1',
      },
    ];
  }

  private getMockVersions(engine: DatabaseEngine): string[] {
    switch (engine) {
      case DatabaseEngine.POSTGRESQL:
        return ['16.1', '15.5', '14.10', '13.13'];
      case DatabaseEngine.MYSQL:
        return ['8.0.35', '8.0.34', '8.0.33'];
      case DatabaseEngine.MONGODB:
        return ['7.0.4', '6.0.12', '5.0.23'];
      default:
        return [];
    }
  }
}

export const databaseService = new DatabaseService();
